import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from "react-router-dom";

const WritingComponent = ({ initialText = '', initialWordCount = 0 }) => {
    const [wordCounts, setWordCounts] = useState({});
    const [answerTexts, setAnswerTexts] = useState({});
    const [dividerPosition, setDividerPosition] = useState(50);
    const navigate = useNavigate();
    const [examData, setExamData] = useState([]);
    const { id } = useParams();

    useEffect(() => {
        if (!id) return;
        fetch(`/exams/${id}/Exam.json`)
            .then((response) => response.json())
            .then((data) => {
                if (Array.isArray(data.writing)) {
                    setExamData(data.writing); // ✅ Ensure it's an array
                } else {
                    console.error("Error: `listening` is not an array", data.listening);
                }
            })
            .catch((error) => console.error("Error loading exam data:", error));
    }, [id]);

    const handleTextChange = (partIndex, e) => {
        const text = e.target.value;
        setAnswerTexts(prev => ({
            ...prev,
            [partIndex]: text
        }));
        const words = text.trim().split(/\s+/);
        setWordCounts(prev => ({
            ...prev,
            [partIndex]: text.trim() === '' ? 0 : words.length
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        navigate("/submit-result");
    };

    const handleMouseDown = (e) => {
        e.preventDefault();
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = (e) => {
        const newDividerPosition = (e.clientX / window.innerWidth) * 100;
        if (newDividerPosition > 20 && newDividerPosition < 80) {
            setDividerPosition(newDividerPosition);
        }
    };

    const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
    };

    return (
        <div className="w-full h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="w-full h-[50px] bg-white flex items-center px-4 border-b border-gray-300 justify-between">
                {/* Logo Section */}
                <div className="flex items-center justify-center h-full bg-gray-200 rounded-md px-4">
                    <Link to={"/student-dashboard"}><span className="text-sm text-gray-700">IELTS Virtual Exam</span></Link>
                </div>

                {/* Centered Text */}
                <div className="flex-1 flex justify-center">
                    <p><b>Writing</b></p>
                </div>

                {/* Navigation Links */}
                <div className="flex space-x-4">
                    <Link to={`/reading/${id}`} className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Reading</Link>
                    <Link to={`/listening/${id}`} className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600">Listening</Link>
                </div>
            </div>


            {/* Main Content */}
            <div className="w-full flex-1 flex flex-col justify-center items-center px-10 py-5">
                <div className="w-full bg-white rounded-lg shadow-lg border border-gray-300 p-6">
                    {examData.length > 0 ? (
                        examData.map((part, partIndex) => (
                            <div key={partIndex} className="mb-6">
                                {/* Part Header */}
                                <div className="w-full bg-gray-200 rounded-lg my-4 p-4">
                                    {/* Part Number and Question */}
                                    <div className="text-lg font-semibold text-gray-800">
                                        Part {part.part}<span className="ml-8"></span> {part.question_text}
                                    </div>
                                    {/* Instruction */}
                                    <div className="text-sm text-gray-600 mt-1 whitespace-pre-line">
                                        {part.instruction}
                                    </div>
                                </div>
                                {/* Content */}
                                <div className="flex my-4 gap-2 items-center relative w-full">
                                    {/* Question Section */}
                                    <div
                                        className="p-4 rounded-lg shadow-inner flex flex-col h-[500px] min-h-0 overflow-y-auto"
                                        style={{ width: `${dividerPosition}%` }}
                                    >
                                        {/* Paragraph Section */}
                                        <div
                                            className="bg-gray-100 p-4 rounded-lg shadow-inner flex-1 overflow-y-auto"
                                        >
                                            <p className="leading-relaxed whitespace-pre-line">{part.paragraph}</p>
                                            {/* Conditionally Render Image */}
                                            {part.image && (
                                                <div className="mt-3 flex justify-center">
                                                    <img
                                                        src={part.image}
                                                        alt="Question"
                                                        className="max-w-full h-auto rounded-lg shadow-md"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>


                                    {/* Draggable Vertical Line */}
                                    <div
                                        className="w-[2px] h-[500px] bg-gray-700 cursor-ew-resize"
                                        onMouseDown={handleMouseDown}
                                    ></div>

                                    {/* Answer Section */}
                                    <div
                                        className="relative rounded-lg shadow-inner"
                                        style={{ width: `${100 - dividerPosition}%`, height: "500px" }}
                                    >
                                        <textarea
                                            className="w-full h-[450px] bg-gray-100 border-none p-2 resize-none focus:outline-none rounded-lg"
                                            onChange={(e) => handleTextChange(partIndex, e)}
                                            value={answerTexts[partIndex] || ''}
                                        />
                                        <div className="absolute right-2 bottom-2 text-xs text-gray-700">
                                            Word Count: {wordCounts[partIndex]}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))) : (
                        <p>Loading...</p>
                    )}
                    {/* Submit Button */}
                    <div className="px-5 pb-5 flex flex-col items-center w-full">
                        <div className="w-full h-px bg-gray-700 mb-4"></div>
                        <div className="w-full flex justify-end">
                            <button
                                className="w-28 h-12 bg-blue-500 text-white rounded-md flex items-center justify-center cursor-pointer hover:bg-blue-600 transition text-lg"
                                onClick={handleSubmit}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div >
            </div >
        </div >
    );
};

export default WritingComponent;
